import React, { useState, useEffect, useMemo } from 'react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import FilterSidebar from './FilterSidebar';
import AdvancedFilterModal from './AdvancedFilterModal';
import { AdvancedFilters, FilterSidebarWrapperProps } from './types';
import './FilterSidebar.css';

const DESKTOP_BREAKPOINT = 1024;

const FilterSidebarWrapper: React.FC<FilterSidebarWrapperProps> = ({
  filters,
  onChange,
  resultCount,
  isLoadingCount = false,
  activePresetId,
  onSelectPreset,
  onSavePreset,
  isPresetModified = false,
  basePresetId = null,
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= DESKTOP_BREAKPOINT);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Count active filters for FAB badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.iv_rank_min !== undefined && filters.iv_rank_min > 0) count++;
    if (filters.iv_rank_max !== undefined && filters.iv_rank_max < 100) count++;
    if (filters.annualized_yield_min !== undefined && filters.annualized_yield_min > 0) count++;
    if (filters.annualized_yield_max !== undefined && filters.annualized_yield_max < 100) count++;
    if (filters.spread_pct_min !== undefined && filters.spread_pct_min > 0) count++;
    if (filters.spread_pct_max !== undefined && filters.spread_pct_max < 30) count++;
    if (filters.market_cap_categories && filters.market_cap_categories.length > 0) count++;
    if (filters.sectors && filters.sectors.length > 0) count++;
    if (filters.net_margin_min !== undefined && filters.net_margin_min > -50) count++;
    if (filters.net_margin_max !== undefined && filters.net_margin_max < 50) count++;
    if (filters.debt_equity_max !== undefined && filters.debt_equity_max < 5) count++;
    if (filters.dividend_yield_min !== undefined && filters.dividend_yield_min > 0) count++;
    if (filters.dividend_yield_max !== undefined && filters.dividend_yield_max < 15) count++;
    if (filters.rsi_min !== undefined && filters.rsi_min > 0) count++;
    if (filters.rsi_max !== undefined && filters.rsi_max < 100) count++;
    if (filters.trend_signals && filters.trend_signals.length > 0) count++;
    return count;
  }, [filters]);

  // Handle mobile drawer apply
  const handleMobileApply = (newFilters: AdvancedFilters) => {
    onChange(newFilters);
    setIsMobileDrawerOpen(false);
  };

  // Desktop: Render permanent sidebar
  if (isDesktop) {
    return (
      <FilterSidebar
        filters={filters}
        onChange={onChange}
        resultCount={resultCount}
        isLoadingCount={isLoadingCount}
        activePresetId={activePresetId}
        onSelectPreset={onSelectPreset}
        onSavePreset={onSavePreset}
        isPresetModified={isPresetModified}
        basePresetId={basePresetId}
      />
    );
  }

  // Mobile: Render FAB + Drawer
  return (
    <>
      {/* Floating Action Button */}
      <button
        className="filter-fab"
        onClick={() => setIsMobileDrawerOpen(true)}
        aria-label="Open filters"
      >
        <AdjustmentsHorizontalIcon className="filter-fab-icon" />
        {activeFilterCount > 0 && (
          <span className="filter-fab-badge">{activeFilterCount}</span>
        )}
      </button>

      {/* Mobile Drawer (reuses existing AdvancedFilterModal) */}
      <AdvancedFilterModal
        isOpen={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        onApply={handleMobileApply}
        initialFilters={filters}
      />
    </>
  );
};

export default FilterSidebarWrapper;
